import { useCallback, ChangeEvent, useMemo } from 'react';
import { AddQuotaButton, Slider } from 'components';
import { ComponentProps } from '@app/participant-fields';
import { Criterion } from 'queries';
import { setCriterionValues } from 'lib/criteria';
import { useAddQuotaButtonHandler } from './useCriterionQuotasHooks';
import { CriterionRangeQuotaList } from './CriterionRangeQuotaList';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  criterionContainer: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  quotaContainer: {
    border: `1px solid ${theme.colors.grayBorder}`,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase,
  },
}));

export const CriterionRange = ({
  field,
  onChange,
  value: criterion,
  allowedFields,
  criteria,
  numberOfParticipantRequired,
  ...props
}: ComponentProps<Criterion>) => {
  const classes = useStyles();
  const { step, label, min, max } = field.range || {};
  const { min: validationMin, max: validationMax } = field.validation || {};

  const showQuotas = !!criterion.quotas;
  const handleQuotasButton = useAddQuotaButtonHandler({
    showQuotas,
    criterion,
    onChange,
    numberOfParticipantRequired,
  });

  const __onChange = useCallback(
    (_e: ChangeEvent<{}>, val: number[]): void => {
      const newCriterion = setCriterionValues(
        criterion,
        [
          {
            min: (val[0] === min ? validationMin : val[0]) || null,
            max: (val[1] === max ? validationMax : val[1]) || null,
          },
        ],
        [
          {
            ref: '',
            label: '',
            value: {
              min: validationMin || null,
              max: validationMax || null,
            },
          },
        ],
      );
      onChange(newCriterion);
    },
    [criterion, onChange, field, min, max, validationMax, validationMin],
  );

  const value = useMemo(() => {
    if (!criterion.allowList) {
      return [min, max];
    }
    if (criterion.allowList.length === 0) {
      return [min, min];
    }
    const val = criterion.allowList[0];
    if (typeof val !== 'object' || 'location' in val) {
      return [min, min];
    }
    return [val.min === null ? min : val.min, val.max === null ? max : val.max];
  }, [criterion, min, max]);

  const marks = useMemo(() => {
    if (min === undefined || max === undefined || !step) {
      return undefined;
    }
    const marks = [{ value: min }];
    const start = min % step === 0 ? min + step : min + (step - (min % step));

    for (let i = start; i <= max; i += step) {
      marks.push({ value: i });
    }
    return marks;
  }, [min, max, step]);

  return (
    <>
      <div className={classes.criterionContainer}>
        <Slider
          {...props}
          min={min}
          max={max}
          step={null}
          onChange={__onChange}
          marks={marks}
          value={value}
          label={label || field.label}
          rightAction={
            <AddQuotaButton value={showQuotas} onClick={handleQuotasButton} />
          }
        />
      </div>
      {showQuotas && (
        <div className={classes.quotaContainer}>
          <CriterionRangeQuotaList
            criterion={criterion}
            numberOfParticipantRequired={numberOfParticipantRequired}
            onChange={onChange}
          />
        </div>
      )}
    </>
  );
};
